
import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { getAllUsers, useCreateAdmin, useUpdateAdmin, useRemoveAdmin } from '../../api/user'
import { getUserRoles, useCreateUserRole, useUpdateUserRole, useDeleteUserRole } from '../../api/user_roles'
import { useQuery } from '@tanstack/react-query'

const AdminSettings = () => {

    const [users, setUsers] = useState([])
    const [updatingUsers, setUpdatingUsers] = useState({})

    const successBtn = useRef()
    const removeBtn = useRef()
    const roleSuccess = useRef()
    const errorBtn = useRef()
    const uid = localStorage.getItem('aid')

    const [roles, setRoles] = useState([])
    const {data: usersData, loading} = useQuery({
        queryKey: ['users'],
        queryFn:  async () => await getAllUsers(),
    })
    const {data: rolesData, isLoading: rolesLoading} = useQuery({
        queryKey: ['roles'],
        queryFn: async () => await getUserRoles(),
        select: (data) => data,
    })
    const createRole = useCreateUserRole();
    const updateRole = useUpdateUserRole();
    const deleteRole = useDeleteUserRole();
    const updateAdmin = useUpdateAdmin(
        () => {
            setUpdatingUsers(prev => {
                const newState = {...prev};
                Object.keys(newState).forEach(key => {
                    newState[key] = false;
                });
                return newState;
            });
        },
        (error) => {
            console.error('Error updating admin roles:', error);
            setErrorTitle('Role Update Failed');
            setErrorMessage('Failed to update user role. Please try again.');
            errorBtn.current.click();
            
            setUpdatingUsers(prev => {
                const newState = {...prev};
                Object.keys(newState).forEach(key => {
                    newState[key] = false;
                });
                return newState;
            });
        }
    );
    
    const removeAdminMutation = useRemoveAdmin(
        () => {
            setRemoveBtnText('Remove');
            successBtn.current.click();
        },
        (error) => {
            console.error('Error removing admin:', error);
            setRemoveBtnText('Remove');
            setErrorTitle('Admin Removal Failed');
            setErrorMessage('Failed to remove admin user. Please try again.');
            errorBtn.current.click();
        }
    );

    const [errorMessage, setErrorMessage] = useState('');
    const [errorTitle, setErrorTitle] = useState('Error');

    const createAdmin = useCreateAdmin(
        () => successBtn.current.click(),
        (error) => {
            setAddBtnText('Submit');
            const responseError = error.response?.data;
            
            if (responseError) {
                setErrorTitle(responseError.message || 'Admin Creation Failed');
                
                // Handle different error types
                if (responseError.error === 'Email already exists') {
                    setErrorMessage('An account with this email address already exists.');
                } else if (responseError.error === 'Admin role not found') {
                    setErrorMessage('The selected admin role does not exist. Please select a valid role.');
                } else if (responseError.errors) {
                    // Handle validation errors
                    setErrorMessage(responseError.errors.map(err => err.message).join('\n'));
                } else {
                    setErrorMessage(responseError.error || 'Failed to create admin user.');
                }
            } else {
                setErrorTitle('Admin Creation Failed');
                setErrorMessage('An unexpected error occurred. Please try again later.');
            }
            
            errorBtn.current.click();
        }
    );

    useEffect(() => {
        if(usersData){
            console.log(usersData, "usersData")
            const filteredUsers = usersData.filter(user => user.role === 'admin')
            setUsers(filteredUsers)
            
            const updatingStatus = {};
            filteredUsers.forEach(user => {
                updatingStatus[user.user_id] = false;
            });
            setUpdatingUsers(updatingStatus);
        }
        if(rolesData){
            console.log(rolesData)
            setRoles(rolesData)
            setAdminRole(rolesData[0].role_id)
            setAdminRoleName(rolesData[0].name)
        }
    }, [usersData, rolesData])


    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [adminRole, setAdminRole] = useState('')
    const [adminRoleName, setAdminRoleName] = useState('-')
    const [removeKey, setRemoveKey] = useState('')
    const [features, setFeatures] = useState([])
    const [selectRole, setSelectRole] = useState('New Role')
    const [roleName, setRoleName] = useState('')
    const [roleKey, setRoleKey] = useState('')
    const [addBtnText, setAddBtnText] = useState('Submit')
    const [removeBtnText, setRemoveBtnText] = useState('Remove')

    const [error, setError] = useState('')

    const handleError = (errorMsg) => {
        setError(errorMsg)
        setTimeout(() => {
            setError('')
        }, 3000);
    }

    const findUserRole = (role) => {
        const roleExist = roles.find(item => item.role_id === role)
        return roleExist ? roleExist.name : '-'
    }

    const updateUserRole = (userId, roleId, roleName) => {
        setUsers(prevUsers => 
            prevUsers.map(user => 
                user.user_id === userId 
                    ? {...user, admin_role_id: roleId} 
                    : user
            )
        );
    }

    const handleUpdateUserRole = (userId) => {
        const user = users.find(u => u.user_id === userId);
        if (!user) return;

        setUpdatingUsers(prev => ({...prev, [userId]: true}));
        
        updateAdmin.mutate(
            { 
                userId: userId, 
                data: { admin_role_id: user.admin_role_id }
            }
        );
    }

    const handleCheck = (feature) => {
        const isCheck = features.some(item => item === feature)
        if (isCheck) {
            setFeatures(prevFeatures => prevFeatures.filter(item => item !== feature))
        } else {
            setFeatures(prevFeatures => [...prevFeatures, feature])
        }
    }

    const handleSelectRole = (role) => {
        setSelectRole(role.name)
        setRoleName(role.name === 'New Role' ? '' : role.name)
        setRoleKey(role.role_id || '')
        setFeatures(role.role_features || [])
    }

    const submitRole = () => {
        if (roleKey !== '') {
            updateRole.mutate({ 
                roleId: roleKey, 
                roleData: { name: roleName, role_features: features } 
            }, {
                onSuccess: () => {
                    roleSuccess.current.click();
                },
                onError: (error) => {
                    console.error('Error updating role:', error);
                    setErrorTitle('Role Update Failed');
                    setErrorMessage('Failed to update role. Please try again.');
                    errorBtn.current.click();
                }
            });
        } else {
            createRole.mutate({
                name: roleName,
                role_features: features
            }, {
                onSuccess: () => {
                    roleSuccess.current.click();
                },
                onError: (error) => {
                    console.error('Error creating role:', error);
                    setErrorTitle('Role Creation Failed');
                    setErrorMessage('Failed to create role. Please try again.');
                    errorBtn.current.click();
                }
            });
        }
    }
    
    const removeRole = () => {
        deleteRole.mutate(roleKey, {
            onSuccess: () => {
                roleSuccess.current.click();
            },
            onError: (error) => {
                console.error('Error deleting role:', error);
                setErrorTitle('Role Deletion Failed');
                setErrorMessage('Failed to delete role. This role may be assigned to users.');
                errorBtn.current.click();
            }
        });
    }

    const addAdmin = (e) => {
        e.preventDefault();
        setAddBtnText('Please Wait...');
        
        createAdmin.mutate({
            email,
            password,
            firstName,
            lastName,
            admin_role_id: adminRole
        }, {
            onSettled: () => setAddBtnText('Submit')
        });
    }

    const removeAdmin = () => {
        setRemoveBtnText('Please Wait...');
        removeAdminMutation.mutate(removeKey);
    }

  return (
    <div className="admin-page py-5">
        <div className='container'>
            <h1 className='dash-title'>Settings</h1>
            <div className="row">
                <div className="col-12 mt-4">
                    <div className="dash-card">
                        <div className="dash-card-heading">
                            <h2 className="dash-card-title">Users</h2>
                            <div className="d-flex flex-wrap">
                                <button 
                                    className="dash-card-heading-btn"
                                    data-bs-toggle="modal"
                                    data-bs-target="#roleModal"
                                >
                                    <i className="fi fi-rr-plus me-2"></i>
                                    Add Role
                                </button>
                                <button 
                                    className={`dash-card-heading-btn ms-2 ${roles.length === 0 && 'disabled'}`}
                                    data-bs-toggle="modal"
                                    data-bs-target="#adminModal"
                                    disabled={roles.length === 0 ? true : false}
                                >
                                    <i className="fi fi-rr-plus me-2"></i>
                                    Add Admin
                                </button>
                            </div>
                        </div>
                        <div className="table-responsive mt-3">
                            <table className="table dash-table">
                                <thead>
                                    <tr>
                                        <td>User</td>
                                        <td>Role</td>
                                        <td className='text-center'>Action</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        users.map((user, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <div className="table-item">
                                                        {user.email}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-item">
                                                        <div className="dropdown dash-form-dropdown">
                                                            <button className="dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                                                {findUserRole(user.admin_role_id)}
                                                            </button>
                                                            <ul className="dropdown-menu">
                                                                {
                                                                    roles.map((role, i) => (
                                                                        <li key={i} onClick={() => updateUserRole(user.user_id, role.role_id, role.name)}>{role.name}</li>
                                                                    ))
                                                                }
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-item d-flex justify-content-center">
                                                        <button 
                                                            className="table-action-btn a-green me-1"
                                                            onClick={() => handleUpdateUserRole(user.user_id)}
                                                            disabled={updatingUsers[user.user_id]}
                                                            title="Save role change"
                                                        >
                                                            {updatingUsers[user.user_id] ? (
                                                                <i className="fi fi-rr-spinner"></i>
                                                            ) : (
                                                                <i className="fi fi-rr-check"></i>
                                                            )}
                                                        </button>
                                                        <button 
                                                            className="table-action-btn a-red me-1"
                                                            data-bs-toggle="modal"
                                                            data-bs-target="#confirmModal"
                                                            onClick={() => setRemoveKey(user.user_id)}
                                                            title="Remove admin user"
                                                        >
                                                            <i className="fi fi-br-cross"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                
            </div>


        </div>


        <div className="modal fade" id="roleModal" tabindex="-1" aria-labelledby="roleModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h1 className="modal-title fs-5" id="artist2ModalLabel">Add Role</h1>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="row">
                            <div className="col-12">
                                <div className="dash-form-group">
                                    <label>Select Role</label>
                                    <div className="dropdown dash-form-dropdown">
                                        <button className="dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                            {selectRole}
                                        </button>
                                        <ul className="dropdown-menu">
                                            <li onClick={() => handleSelectRole({name: 'New Role', key: '', features: []})}>New Role</li>
                                            {
                                                roles.map((role, i) => (
                                                    <li key={i} onClick={() => handleSelectRole(role)}>{role.name}</li>
                                                ))
                                            }
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 mt-3">
                                <div className="dash-form-group">
                                    <label>Role Name</label>
                                    <input 
                                        type="text" 
                                        className='dash-form-input' 
                                        onChange={e => setRoleName(e.target.value)} 
                                        value={roleName} 
                                        placeholder='Role Name' 
                                    />
                                </div>
                            </div>
                            <div className="col-12 mt-3">
                                <div className="dash-form-group">
                                    <label className='mb-2'>Features</label>
                                    <div className="dash-form-check d-flex">
                                        <input 
                                            type="checkbox" 
                                            id='role1' 
                                            className='form-check-input' 
                                            onChange={() => handleCheck('Users')}
                                            checked={features.some(item => item === 'Users')}
                                        />
                                        <label className='ms-2' htmlFor='role1'>Users</label>
                                    </div>
                                    <div className="dash-form-check d-flex">
                                        <input 
                                            type="checkbox" 
                                            id='role2' 
                                            className='form-check-input' 
                                            onChange={() => handleCheck('Payout Requests')}
                                            checked={features.some(item => item === 'Payout Requests')}
                                        />
                                        <label className='ms-2' htmlFor='role2'>Payout Requests</label>
                                    </div>
                                    <div className="dash-form-check d-flex">
                                        <input 
                                            type="checkbox" 
                                            id='role3' 
                                            className='form-check-input' 
                                            onChange={() => handleCheck('Releases')}
                                            checked={features.some(item => item === 'Releases')}
                                        />
                                        <label className='ms-2' htmlFor='role3'>Releases</label>
                                    </div>
                                    <div className="dash-form-check d-flex">
                                        <input 
                                            type="checkbox" 
                                            id='role4' 
                                            className='form-check-input' 
                                            onChange={() => handleCheck('Posts')}
                                            checked={features.some(item => item === 'Posts')}
                                        />
                                        <label className='ms-2' htmlFor='role4'>Posts</label>
                                    </div>
                                    <div className="dash-form-check d-flex">
                                        <input 
                                            type="checkbox" 
                                            id='role5' 
                                            className='form-check-input' 
                                            onChange={() => handleCheck('Labels')}
                                            checked={features.some(item => item === 'Labels')}
                                        />
                                        <label className='ms-2' htmlFor='role5'>Labels</label>
                                    </div>
                                    <div className="dash-form-check d-flex">
                                        <input 
                                            type="checkbox" 
                                            id='role6' 
                                            className='form-check-input' 
                                            onChange={() => handleCheck('Artists')}
                                            checked={features.some(item => item === 'Artists')}
                                        />
                                        <label className='ms-2' htmlFor='role6'>Artists</label>
                                    </div>
                                    <div className="dash-form-check d-flex">
                                        <input 
                                            type="checkbox" 
                                            id='role7' 
                                            className='form-check-input' 
                                            onChange={() => handleCheck('Upload Reports')}
                                            checked={features.some(item => item === 'Upload Reports')}
                                        />
                                        <label className='ms-2' htmlFor='role7'>Upload Reports</label>
                                    </div>
                                    <div className="dash-form-check d-flex">
                                        <input 
                                            type="checkbox" 
                                            id='role8' 
                                            className='form-check-input' 
                                            onChange={() => handleCheck('Analytics')}
                                            checked={features.some(item => item === 'Analytics')}
                                        />
                                        <label className='ms-2' htmlFor='role8'>Analytics</label>
                                    </div>
                                    <div className="dash-form-check d-flex">
                                        <input 
                                            type="checkbox" 
                                            id='role9' 
                                            className='form-check-input' 
                                            onChange={() => handleCheck('Reports History')}
                                            checked={features.some(item => item === 'Reports History')}
                                        />
                                        <label className='ms-2' htmlFor='role9'>Reports History</label>
                                    </div>
                                    <div className="dash-form-check d-flex">
                                        <input 
                                            type="checkbox" 
                                            id='role10' 
                                            className='form-check-input' 
                                            onChange={() => handleCheck('Platforms')}
                                            checked={features.some(item => item === 'Platforms')}
                                        />
                                        <label className='ms-2' htmlFor='role10'>Platforms</label>
                                    </div>
                                    <div className="dash-form-check d-flex">
                                        <input 
                                            type="checkbox" 
                                            id='role11' 
                                            className='form-check-input' 
                                            onChange={() => handleCheck('Roles / Genres')}
                                            checked={features.some(item => item === 'Roles / Genres')}
                                        />
                                        <label className='ms-2' htmlFor='role11'>Roles / Genres</label>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 mt-3">
                                <div className="d-flex justify-content-between flex-wrap">
                                    <button 
                                        className={`dash-form-btn ${selectRole === 'New Role' ? 'disabled' : ''}`}
                                        disabled={selectRole === 'New Role' ? true : false}
                                        onClick={removeRole}
                                    >Delete Role</button>
                                    <div className="dash-form-btns">
                                        <button 
                                            className='dash-form-btn me-2'
                                            data-bs-dismiss="modal"
                                        >Cancel</button>
                                        <button 
                                            className={`dash-form-btn ${roleName === '' || features.length === 0 ? 'disabled' : ''}`}
                                            disabled={roleName === '' || features.length === 0 ? true : false}
                                            onClick={submitRole}
                                        >Done</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <button ref={roleSuccess} data-bs-target="#roleSuccessModal" data-bs-toggle="modal" hidden></button>
        </div>

        <div className="modal fade" id="adminModal" tabindex="-1" aria-labelledby="adminModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h1 className="modal-title fs-5" id="artist2ModalLabel">Add Admin</h1>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <form onSubmit={addAdmin} className="row">
                            <div className="col-md-6 mt-3">
                                <div className="dash-form-group">
                                    <label>First Name</label>
                                    <input 
                                        type="text" 
                                        placeholder='First Name' 
                                        className='dash-form-input' 
                                        onChange={e => setFirstName(e.target.value)}
                                        value={firstName}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-md-6 mt-3">
                                <div className="dash-form-group">
                                    <label>Last Name</label>
                                    <input 
                                        type="text" 
                                        placeholder='Last Name' 
                                        className='dash-form-input' 
                                        onChange={e => setLastName(e.target.value)}
                                        value={lastName}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-md-6 mt-3">
                                <div className="dash-form-group">
                                    <label>Email</label>
                                    <input 
                                        type="email" 
                                        placeholder='Email' 
                                        className='dash-form-input' 
                                        onChange={e => setEmail(e.target.value)}
                                        value={email}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-md-6 mt-3">
                                <div className="dash-form-group">
                                    <label>Password</label>
                                    <input 
                                        type="password" 
                                        placeholder='Password' 
                                        className='dash-form-input' 
                                        onChange={e => setPassword(e.target.value)}
                                        value={password}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-md-6 mt-3">
                                <div className="dash-form-group">
                                    <label>Role</label>
                                    <div className="dropdown dash-form-dropdown">
                                        <button className="dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                            {adminRoleName}
                                        </button>
                                        <ul className="dropdown-menu">
                                            {
                                                roles.map((role, i) => (
                                                    <li key={i} onClick={() => {setAdminRole(role.role_id); setAdminRoleName(role.name)}}>{role.name}</li>
                                                ))
                                            }
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-12 mt-3">
                                {
                                    error !== '' && <h5 className='text-danger text-center mb-3'>{error}</h5>
                                }
                                <div className="dash-form-btns">
                                    <button 
                                        type="submit"
                                        className={`dash-form-btn ${addBtnText === 'Please Wait...' && 'disabled'}`}
                                        disabled={addBtnText === 'Please Wait...' ? true : false}
                                    >{addBtnText}</button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>


        <button ref={successBtn} data-bs-target="#successModal" data-bs-toggle="modal" hidden></button>
        <div className="modal fade" id="successModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="successModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-body success-modal">
                        <Link className='success-close-btn' to="/admin/settings" data-bs-dismiss="modal">
                            <i className="fi fi-rr-cross"></i>
                        </Link>
                        <i className="fi fi-rr-check-circle text-success success-icon"></i>
                        <h5>Operation Completed Successfully!</h5>
                    </div>
                </div>
            </div>
        </div>


        <div className="modal fade" id="roleSuccessModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="roleSuccessModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-body success-modal">
                        <Link className='success-close-btn' to="/admin/settings" data-bs-dismiss="modal">
                            <i className="fi fi-rr-cross"></i>
                        </Link>
                        <i className="fi fi-rr-check-circle text-success success-icon"></i>
                        <h5>Roles Saved Successfully!</h5>
                    </div>
                </div>
            </div>
        </div>


        <div className="modal fade" id="confirmModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="confirmModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-body success-modal">
                        <Link className='success-close-btn' to="#" data-bs-dismiss="modal">
                            <i className="fi fi-rr-cross"></i>
                        </Link>
                        <h5>Are you sure you want to remove this user?</h5>
                        <div className="d-flex justify-content-center mt-4">
                            <button 
                                className="dash-card-heading-btn cancel-btn share-btn me-2" 
                                data-bs-dismiss="modal"
                            >
                                Cancel
                            </button>
                            <button 
                                className={`dash-card-heading-btn share-btn ${removeBtnText === 'Please Wait...' && 'disabled'}`} 
                                onClick={removeAdmin}
                                disabled={removeBtnText === 'Please Wait...' ? true : false}
                            >{removeBtnText}</button>
                            <button ref={removeBtn} data-bs-target="#successModal" data-bs-toggle="modal" hidden></button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <button ref={errorBtn} data-bs-target="#errorModal" data-bs-toggle="modal" hidden></button>
        <div className="modal fade" id="errorModal" tabIndex="-1" data-bs-backdrop="static" aria-labelledby="errorModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-body error-modal">
                        <button type="button" className="success-close-btn" data-bs-dismiss="modal" style={{ position: 'absolute', right: '15px', top: '15px', background: 'none', border: 'none' }}>
                            <i className="fi fi-rr-cross"></i>
                        </button>
                        <div className="text-center mb-3">
                            <i className="fi fi-rr-cross-circle text-danger error-icon" style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block', margin: '0 auto' }}></i>
                            <h5 className="text-center mt-3">{errorTitle}</h5>
                            <p className="mt-3 text-center">{errorMessage}</p>
                        </div>
                        <div className="d-flex justify-content-center mt-4">
                            <button type="button" className="dash-form-btn" data-bs-dismiss="modal">Close</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>
  )
}

export default AdminSettings